/**
 * Public /validate landing — Bucket Q2.5.
 *
 * NOT inside the authenticated PortalShell. This is the recipient-side
 * surface: someone with a token (and optionally an email) can validate
 * here. The page is intentionally minimal — it doesn't reveal anything
 * about the share until validation succeeds.
 *
 * Flow:
 *   1. Recipient enters token + email.
 *   2. Submit → POST /api/v1/validate (always returns 202, no leak).
 *   3. UI shows "if your token + email match, we sent you a link".
 *   4. Recipient clicks email link → server-side /api/v1/validate/confirm
 *      sets the share-session cookie + redirects to /validate/success.
 *   5. /validate/success fetches the payload via cookie and renders
 *      the report (or a fallback for anonymous-open shares whose
 *      session was minted directly without an email step).
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { submitValidationApiV1ValidatePost } from "@hackereyes/api-client";
import "./validate-page.css";

type Stage = "form" | "submitted" | "anonymous_session";

export function ValidatePage() {
  const [params] = useSearchParams();
  // Anonymous-open links pass the token in the query string. Pre-fill
  // the field so the recipient just clicks Submit.
  const initialToken = params.get("token") ?? "";

  const [token, setToken] = useState(initialToken);
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<Stage>("form");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const { data, error } = await submitValidationApiV1ValidatePost({
        body: {
          token: token.trim(),
          email: email.trim() || undefined,
        } as never,
      });
      if (error) {
        const detail = (error as Record<string, unknown>)?.detail as string;
        throw new Error(detail || "submit_failed");
      }
      // Anonymous-open path returns a session-minted response with
      // expires_at; the cookie has been set already. Redirect into
      // /validate/success directly.
      const status = (data as Record<string, unknown>)?.status as string | undefined;
      if (status === "anonymous_session_minted") {
        setStage("anonymous_session");
        // The grant_id isn't exposed by the anti-enumeration response
        // shape; the cookie carries it. Push the user to a generic
        // viewer route that pulls the active session via cookie.
        window.location.href = "/validate/success";
        return;
      }
      setStage("submitted");
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="validate-page">
      <div className="validate-inner">
        <div className="validate-header">
          <div className="eyebrow">Recipient validation</div>
          <h1 className="validate-title">/Recon report access</h1>
          <p className="validate-lede">
            You've received a token to view a /Recon assessment report.
            Enter the token and your work email below — we'll send a
            one-time confirmation link to verify you control the email.
          </p>
        </div>

        {stage === "form" && (
          <form className="validate-form" onSubmit={onSubmit}>
            {errorMsg && <div className="validate-error">{errorMsg}</div>}

            <label className="validate-field">
              <span className="validate-label">Access token</span>
              <input
                type="text"
                className="validate-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="ABC-...-XYZ"
                required
                autoComplete="off"
                spellCheck={false}
              />
            </label>

            <label className="validate-field">
              <span className="validate-label">
                Work email <span className="validate-label-hint">(if required)</span>
              </span>
              <input
                type="email"
                className="validate-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                autoComplete="email"
              />
            </label>

            <button
              type="submit"
              className="recon-btn recon-btn-primary validate-submit"
              disabled={submitting || !token.trim()}
            >
              {submitting ? "Verifying…" : "Verify"}
            </button>

            <p className="validate-footnote">
              Don't have a token? Reach out to the company that asked
              you to verify their security posture — they'll generate
              one for you.
            </p>
          </form>
        )}

        {stage === "submitted" && (
          <div className="validate-submitted">
            <h2 className="validate-submitted-title">Check your inbox.</h2>
            <p className="validate-submitted-body">
              If your token and email match a valid share, we just sent
              you a one-time confirmation link. Click it to view the
              report. The link expires in 8 hours.
            </p>
            <p className="validate-submitted-body">
              No email arriving? Confirm with the sender that they used
              the right token + your email is on their trusted list,
              then{" "}
              <button
                type="button"
                className="validate-link-button"
                onClick={() => {
                  setStage("form");
                  setErrorMsg(null);
                }}
              >
                try again
              </button>
              .
            </p>
          </div>
        )}

        {stage === "anonymous_session" && (
          <div className="validate-submitted">
            <h2 className="validate-submitted-title">Loading report…</h2>
            <p className="validate-submitted-body">
              Public-link access — redirecting you to the report.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
